/**
 * Cadernos extras do miolo.
 *
 * Cada caderno é uma sequência de páginas com layout próprio: listas (contatos,
 * senhas, finanças), fichas (emergência, receitas, treino, estudos), cartões
 * (orações), pautas (caderno simples, caligrafia, partitura) e imagens (desenho).
 *
 * Tudo é calculado em milímetros a partir da altura útil da página, então o
 * mesmo caderno funciona em A4, A5 e A6 — mudando a densidade, não o desenho.
 */
import { PAPEIS } from './papel.js';
import { enfeitesDePauta } from './motivos.js';
import { caixa, caligrafia as blocoCaligrafia, campos, checklist, coluna, espaco, imagem as blocoImagem, linhas as blocoLinhas, partitura as blocoPartitura, secao, separador, tabela, texto, } from './blocos.js';
import { planoDeLeitura } from '../content/biblia.js';
import { GUIA_PROMPTS } from '../content/prompts.js';
import { ROTULO_CADERNO, totalDeFolhas } from '../types/planner.js';
export const ALTURA_CABECALHO_PAGINA_MM = 7;
export const ALTURA_RODAPE_MM = 5.5;
/**
 * Reserva de altura para uma seção de título de caderno.
 *
 * Serve para o cálculo das linhas que cabem numa página (título + subtítulo),
 * não para limitar a seção: a seção abraça o próprio conteúdo, então nada é
 * cortado em nenhum formato. O valor é o pior caso — em A6 o subtítulo quebra em
 * mais linhas — e sobra folga de propósito.
 */
export const ALTURA_TITULO_SECAO_MM = 28;
export const ESPECIFICACOES = {
    CONTATOS: {
        colunas: ['Nome', 'E-mail', 'Telefone', 'Chave PIX'],
        larguras: [0.28, 0.3, 0.18, 0.24],
        camposDaFicha: ['Nome', 'E-mail', 'Telefone', 'Chave PIX'],
        titulo: 'Contatos',
        descricao: 'Nome, e-mail, telefone e chave PIX para transferências.',
        paginas: 2,
        alturaLinhaExtraMm: 1,
        comTitulo: true,
    },
    SENHAS: {
        colunas: ['Site ou serviço', 'Endereço', 'Usuário', 'Senha'],
        larguras: [0.24, 0.3, 0.24, 0.22],
        camposDaFicha: ['Site ou serviço', 'Endereço', 'Usuário', 'Senha'],
        titulo: 'Senhas e acessos',
        descricao: 'Anote com caneta e guarde a agenda em lugar seguro.',
        paginas: 2,
        alturaLinhaExtraMm: 1,
        comTitulo: true,
    },
    FINANCAS: {
        colunas: ['Data', 'Descrição', 'Categoria', 'Valor'],
        larguras: [0.14, 0.44, 0.24, 0.18],
        camposDaFicha: ['Data', 'Descrição', 'Categoria', 'Valor'],
        titulo: 'Controle de gastos',
        descricao: 'Um registro por dia, com o total da página no fim.',
        paginas: 4,
        comTitulo: true,
    },
    CONVIDADOS: {
        colunas: ['Nome', 'Contato', 'Confirmado', 'Mesa'],
        larguras: [0.34, 0.24, 0.18, 0.24],
        camposDaFicha: ['Nome', 'Contato', 'Confirmado', 'Mesa'],
        titulo: 'Lista de convidados',
        descricao: 'Nome, contato, se confirmou e em que mesa senta.',
        paginas: 2,
        alturaLinhaExtraMm: 1,
        comTitulo: true,
    },
    PROJETOS: {
        colunas: ['Projeto', 'Responsável', 'Prazo', 'Situação'],
        larguras: [0.34, 0.24, 0.18, 0.24],
        camposDaFicha: ['Projeto', 'Responsável', 'Prazo', 'Situação'],
        titulo: 'Painel de projetos',
        descricao: 'Situação de cada projeto de uma olhada só. Use as cores para marcar o que está crítico.',
        paginas: 3,
        alturaLinhaExtraMm: 2,
        comTitulo: true,
    },
    ORCAMENTOS: {
        colunas: ['Cliente', 'Item', 'Valor', 'Validade', 'Situação'],
        larguras: [0.24, 0.3, 0.18, 0.14, 0.14],
        camposDaFicha: ['Cliente', 'Item', 'Valor', 'Situação'],
        titulo: 'Orçamentos',
        descricao: 'Propostas enviadas, valor, validade e se foram aprovadas.',
        paginas: 3,
        alturaLinhaExtraMm: 2,
        comTitulo: true,
    },
    ESTOQUE: {
        colunas: ['Item', 'Qtd', 'Mínimo', 'Validade', 'Fornecedor'],
        larguras: [0.3, 0.12, 0.14, 0.16, 0.28],
        camposDaFicha: ['Item', 'Qtd', 'Mínimo', 'Fornecedor'],
        titulo: 'Estoque e materiais',
        descricao: 'O que tem, o que está acabando e de quem comprar. Ideal para esmaltes, insumos e materiais.',
        paginas: 2,
        alturaLinhaExtraMm: 1,
        comTitulo: true,
    },
};
/** Pauta sugerida para páginas de reunião. */
export const PAUTA_DE_REUNIAO = [
    'Avisos gerais',
    'Pendências da semana anterior',
    'Decisões tomadas',
    'Próximos passos e responsáveis',
];
/** Horários sugeridos para a grade escolar. */
export const HORARIOS_ESCOLARES = [
    '07:00',
    '08:00',
    '09:00',
    '10:00',
    '11:00',
    '13:00',
    '14:00',
    '15:00',
    '16:00',
];
/** Dias da semana usados na grade de horário. */
export const DIAS_DA_GRADE = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex'];
/** Matérias sugeridas para a tabela do horário escolar. */
export const MATERIAS_ESCOLARES = [
    'Português',
    'Matemática',
    'Ciências',
    'História',
    'Geografia',
    'Inglês',
    'Arte',
    'Educação física',
];
export function criarCadernoPadrao(tipo, formato, paginas) {
    const especificacao = ESPECIFICACOES[tipo];
    const papel = PAPEIS[formato];
    const alturaLinha = papel.alturaLinhaPadraoMm;
    return {
        id: `caderno-${tipo.toLowerCase()}-${Math.random().toString(36).slice(2, 8)}`,
        tipo,
        titulo: especificacao?.titulo ?? ROTULO_CADERNO[tipo],
        /**
         * O número de páginas é sempre pelo menos 1. Nos cadernos automáticos
         * (leitura da Bíblia, desenho) ele é só um rótulo — a contagem real vem do
         * conteúdo — mas zerado ele reprova na validação e o miolo não abre.
         */
        paginas: Math.max(1, paginas ?? especificacao?.paginas ?? paginasSugeridas(tipo)),
        imagens: [],
        paginasEmBranco: tipo === 'DESENHO' ? 4 : 0,
        estiloPauta: 'LINHA',
        alturaLinhaMm: alturaLinha,
        mostrarData: true,
        estiloCaligrafia: 'CURSIVA',
        alturaLinhaCaligrafiaMm: 16,
        canonBiblia: 'EVANGELICA',
        abreviarLivros: !papel.largo,
        sistemasPorPagina: papel.sistemasDePartitura,
        itens: tipo === 'HORARIO' ? [...HORARIOS_ESCOLARES] : tipo === 'REUNIOES' ? [...PAUTA_DE_REUNIAO] : [],
        materias: [],
        diasDaGrade: [...DIAS_DA_GRADE],
        blocosFolhas: tipo === 'FOLHAS'
            ? [
                { estilo: 'LINHA', traco: 'SOLIDA', paginas: 20, rotulo: 'Pautada' },
                { estilo: 'PONTOS', paginas: 10, rotulo: 'Pontilhada' },
            ]
            : [],
        molduraDesenho: true,
        visivel: true,
    };
}
function paginasSugeridas(tipo) {
    switch (tipo) {
        case 'ORACOES':
            return 8;
        case 'PAUTADO':
            return 10;
        case 'CALIGRAFIA':
            return 6;
        case 'PARTITURA':
            return 8;
        case 'PLANEJAMENTO':
            return 3;
        case 'HORARIO':
            return 1;
        case 'REUNIOES':
            return 4;
        case 'PROMPTS':
            return 6;
        case 'FOLHAS':
            return 30;
        case 'PROJETOS':
        case 'ORCAMENTOS':
            return 3;
        case 'PACIENTES':
        case 'ESTOQUE':
            return 4;
        case 'ESTUDOS':
        case 'TREINO':
        case 'RECEITAS':
            return 4;
        case 'EMERGENCIA':
            return 2;
        case 'HABITOS':
            return 2;
        default:
            return 2;
    }
}
/* ------------------------------------------------------------------ */
/* Utilitários de montagem                                             */
/* ------------------------------------------------------------------ */
function alturaUtil(ctx, comTitulo) {
    const base = ctx.medidas.alturaConteudoMm - ALTURA_CABECALHO_PAGINA_MM - ALTURA_RODAPE_MM - 2.5;
    return comTitulo ? base - ALTURA_TITULO_SECAO_MM : base;
}
function cabecalhoDe(ctx, direita) {
    return { esquerda: ctx.nomeDoMiolo.slice(0, 40), direita: direita?.slice(0, 30), mostrarRegua: true };
}
function rodapeDe(ctx, esquerda) {
    return { esquerda: esquerda.slice(0, 46), mostrarRegua: false };
}
function paginaDeCaderno(ctx, caderno, indice, total, secoes, opcoes = {}) {
    return {
        id: `${caderno.id}-p${indice + 1}`,
        formato: ctx.formato,
        tipo: 'CADERNO',
        titulo: `${caderno.titulo} · ${indice + 1}`,
        meta: {},
        indice: opcoes.rotuloIndice,
        cabecalho: cabecalhoDe(ctx, total > 1 ? `${indice + 1} de ${total}` : undefined),
        rodape: rodapeDe(ctx, caderno.titulo),
        fundo: { tipo: 'COR', temaEscuro: false },
        secoes,
    };
}
function secaoTitulo(titulo, subtitulo) {
    return secao('titulo', [
        texto(titulo, { tamanho: 'XL', peso: 'FORTE', cor: 'PRINCIPAL_ESCURO' }),
        texto(subtitulo, { tamanho: 'XS', cor: 'TEXTO_SUAVE', espacoAcimaMm: 1 }),
        separador({ estilo: 'LINHA', cor: 'DESTAQUE', espessuraMm: 0.4, espacoMm: 3 }),
    ], 
    // Peso 0 sem altura: o renderizador faz a seção abraçar o próprio conteúdo.
    { peso: 0 });
}
/* ------------------------------------------------------------------ */
/* Listas e fichas                                                     */
/* ------------------------------------------------------------------ */
function paginasDeLista(ctx, caderno) {
    const especificacao = ESPECIFICACOES[caderno.tipo];
    const altura = alturaUtil(ctx, especificacao.comTitulo);
    const alturaLinha = caderno.alturaLinhaMm + (especificacao.alturaLinhaExtraMm ?? 0);
    const usarFichas = PAPEIS[ctx.formato].estreito;
    const paginas = [];
    if (!usarFichas) {
        const linhasPorPagina = Math.max(6, Math.floor(altura / alturaLinha));
        for (let i = 0; i < caderno.paginas; i += 1) {
            const secoes = [];
            if (especificacao.comTitulo && i === 0) {
                secoes.push(secaoTitulo(especificacao.titulo, especificacao.descricao));
            }
            secoes.push(secao('lista', [
                tabela(especificacao.colunas, {
                    larguras: especificacao.larguras,
                    linhas: linhasPorPagina,
                    alturaLinhaMm: alturaLinha,
                    flexivel: true,
                    borda: 'SUAVE',
                }),
            ], { peso: 1 }));
            paginas.push(paginaDeCaderno(ctx, caderno, i, caderno.paginas, secoes, {
                rotuloIndice: i === 0 ? especificacao.titulo : undefined,
            }));
        }
        return paginas;
    }
    // A6: cada registro vira uma ficha com campos rotulados.
    const alturaFicha = 5 + caderno.alturaLinhaMm * especificacao.camposDaFicha.length + 8;
    const porPagina = Math.max(1, Math.floor(altura / alturaFicha));
    for (let i = 0; i < caderno.paginas; i += 1) {
        const secoes = [];
        if (especificacao.comTitulo && i === 0) {
            secoes.push(secaoTitulo(especificacao.titulo, especificacao.descricao));
        }
        secoes.push(secao('fichas', Array.from({ length: porPagina }, (_, indice) => caixa({
            conteudo: [
                campos(especificacao.camposDaFicha.map((rotulo) => ({ rotulo, linhas: 1 })), { alturaLinhaMm: caderno.alturaLinhaMm }),
            ],
            fundo: 'SUAVE',
            raioMm: 3,
            preenchimentoMm: 4,
            flexivel: true,
        })), { peso: 1, gapMm: 4 }));
        paginas.push(paginaDeCaderno(ctx, caderno, i, caderno.paginas, secoes, {
            rotuloIndice: i === 0 ? especificacao.titulo : undefined,
        }));
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Emergência                                                          */
/* ------------------------------------------------------------------ */
function paginasDeEmergencia(ctx, caderno) {
    const altura = alturaUtil(ctx, false);
    const alturaLinha = caderno.alturaLinhaMm;
    const paginas = [];
    const total = Math.max(1, caderno.paginas);
    for (let i = 0; i < total; i += 1) {
        const secoes = [];
        if (i === 0) {
            secoes.push(secaoTitulo('Emergência', 'Quem precisa saber, o que precisa saber.'));
            secoes.push(secao('ficha-medica', [], {
                alturaMm: Math.round(altura * 0.44),
                peso: 0,
                gapMm: 4,
                colunas: [
                    coluna(0.5, [
                        caixa({
                            titulo: 'Alergias a medicamentos',
                            conteudo: [blocoLinhas({ alturaLinhaMm: alturaLinha, flexivel: true, rotulo: '' })],
                            fundo: 'DESTAQUE_SUAVE',
                            borda: 'DESTAQUE',
                            raioMm: 3,
                            preenchimentoMm: 4,
                            flexivel: true,
                        }),
                    ]),
                    coluna(0.5, [
                        caixa({
                            titulo: 'Alergias alimentares',
                            conteudo: [blocoLinhas({ alturaLinhaMm: alturaLinha, flexivel: true })],
                            fundo: 'DESTAQUE_SUAVE',
                            borda: 'DESTAQUE',
                            raioMm: 3,
                            preenchimentoMm: 4,
                            flexivel: true,
                        }),
                    ]),
                ],
            }));
            secoes.push(secao('ficha-dados', [
                caixa({
                    titulo: 'Dados médicos',
                    conteudo: [
                        campos([
                            { rotulo: 'Tipo sanguíneo', linhas: 1 },
                            { rotulo: 'Plano de saúde', linhas: 1 },
                            { rotulo: 'Hospital de preferência', linhas: 1 },
                            { rotulo: 'Medicamentos de uso contínuo', linhas: 2 },
                        ], { alturaLinhaMm: alturaLinha }),
                    ],
                    fundo: 'SUAVE',
                    raioMm: 3,
                    preenchimentoMm: 4,
                    flexivel: true,
                }),
            ], { peso: 1 }));
        }
        else {
            const especificacao = {
                colunas: ['Nome', 'Parentesco', 'Telefone', 'Observação'],
                larguras: [0.3, 0.2, 0.22, 0.28],
            };
            const linhasPorPagina = Math.max(6, Math.floor((altura + 16) / (alturaLinha + 1)));
            if (i === 1) {
                secoes.push(secaoTitulo('A quem ligar', 'Em caso de emergência, comece por aqui.'));
            }
            secoes.push(secao('contatos-emergencia', [
                tabela(especificacao.colunas, {
                    larguras: especificacao.larguras,
                    linhas: linhasPorPagina,
                    alturaLinhaMm: alturaLinha + 1,
                    flexivel: true,
                    borda: 'SUAVE',
                    rotulo: i === 1 ? undefined : 'A quem ligar',
                }),
            ], { peso: 1 }));
        }
        paginas.push(paginaDeCaderno(ctx, caderno, i, total, secoes, {
            rotuloIndice: i === 0 ? 'Emergência e alergias' : i === 1 ? 'A quem ligar' : undefined,
        }));
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Diário de orações                                                   */
/* ------------------------------------------------------------------ */
function paginasDeOracoes(ctx, caderno) {
    const comTitulo = true;
    const altura = alturaUtil(ctx, comTitulo);
    const alturaLinha = caderno.alturaLinhaMm;
    const alturaCartao = Math.max(58, alturaLinha * 9);
    const porPagina = Math.max(1, Math.floor(altura / alturaCartao));
    const paginas = [];
    for (let i = 0; i < caderno.paginas; i += 1) {
        const secoes = [];
        if (i === 0) {
            secoes.push(secaoTitulo('Diário de orações', 'Peça, registre e volte para ver como foi respondido.'));
        }
        secoes.push(secao('cartoes', Array.from({ length: porPagina }, () => caixa({
            conteudo: [
                campos([{ rotulo: 'Data', linhas: 1 }], { alturaLinhaMm: alturaLinha }),
                blocoLinhas({
                    alturaLinhaMm: alturaLinha,
                    rotulo: 'Pedido de oração',
                    rotuloCor: 'DESTAQUE_TEXTO',
                    flexivel: true,
                }),
                blocoLinhas({
                    alturaLinhaMm: alturaLinha,
                    rotulo: 'Como foi respondido',
                    flexivel: true,
                }),
            ],
            fundo: 'SUAVE',
            raioMm: 3,
            preenchimentoMm: 4,
            flexivel: true,
        })), { peso: 1, gapMm: 4 }));
        paginas.push(paginaDeCaderno(ctx, caderno, i, caderno.paginas, secoes, {
            rotuloIndice: i === 0 ? 'Diário de orações' : undefined,
        }));
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Leitura da Bíblia                                                   */
/* ------------------------------------------------------------------ */
function paginasDeLeitura(ctx, caderno) {
    const altura = alturaUtil(ctx, true);
    const alturaLinha = Math.max(5, caderno.alturaLinhaMm - 0.5);
    const alturaPrimeira = altura - 21;
    const linhasPrimeira = Math.max(8, Math.floor(alturaPrimeira / alturaLinha));
    const linhasDemais = Math.max(8, Math.floor((altura + 21) / alturaLinha));
    const plano = planoDeLeitura(365, caderno.canonBiblia);
    const paginas = [];
    let cursor = 0;
    let indice = 0;
    while (cursor < plano.length) {
        const limite = indice === 0 ? linhasPrimeira : linhasDemais;
        const lote = plano.slice(cursor, cursor + limite);
        cursor += lote.length;
        const secoes = [];
        if (indice === 0) {
            const total = caderno.canonBiblia === 'CATOLICA' ? '1.334 capítulos' : '1.189 capítulos';
            secoes.push(secaoTitulo('Leitura da Bíblia', `${lote.length ? '' : ''}365 dias · ${total} · marque cada dia lido.`));
        }
        secoes.push(secao('plano', [
            tabela(['Dia', 'Leitura', '✓'], {
                larguras: [0.12, 0.76, 0.12],
                linhas: lote.length,
                celulas: lote.map((leitura) => [
                    String(leitura.dia),
                    caderno.abreviarLivros ? leitura.textoAbreviado : leitura.texto,
                    '',
                ]),
                alturaLinhaMm: alturaLinha,
                flexivel: true,
                borda: 'SUAVE',
            }),
        ], { peso: 1 }));
        paginas.push(paginaDeCaderno(ctx, caderno, indice, 1, secoes, {
            rotuloIndice: indice === 0 ? 'Leitura da Bíblia em 365 dias' : undefined,
        }));
        indice += 1;
    }
    const total = paginas.length;
    paginas.forEach((pagina, posicao) => {
        if (pagina.cabecalho) {
            pagina.cabecalho.direita = `${posicao + 1} de ${total}`;
        }
    });
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Caderno pautado                                                     */
/* ------------------------------------------------------------------ */
function paginasDePauta(ctx, caderno) {
    const altura = alturaUtil(ctx, caderno.mostrarData);
    const alturaLinha = caderno.alturaLinhaMm;
    const paginas = [];
    for (let i = 0; i < caderno.paginas; i += 1) {
        const secoes = [];
        if (caderno.mostrarData) {
            secoes.push(secao('data', [
                campos([{ rotulo: 'Data', linhas: 1 }, { rotulo: 'Assunto', linhas: 1 }], {
                    alturaLinhaMm: Math.max(6, alturaLinha),
                }),
            ], { alturaMm: 16, peso: 0 }));
        }
        secoes.push(secao('pauta', [
            blocoLinhas({
                ...enfeitesDePauta(ctx.escrita),
                alturaLinhaMm: alturaLinha,
                espacamentoMm: ctx.espacamentoMm,
                estilo: caderno.estiloPauta,
                flexivel: true,
                colunas: PAPEIS[ctx.formato].largo ? 2 : 1,
            }),
        ], { peso: 1 }));
        paginas.push(paginaDeCaderno(ctx, caderno, i, caderno.paginas, secoes, {
            rotuloIndice: i === 0 ? caderno.titulo : undefined,
        }));
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Caligrafia                                                          */
/* ------------------------------------------------------------------ */
function paginasDeCaligrafia(ctx, caderno) {
    const altura = alturaUtil(ctx, false);
    const alturaLinha = caderno.alturaLinhaCaligrafiaMm;
    const linhas = Math.max(4, Math.floor(altura / alturaLinha));
    const paginas = [];
    for (let i = 0; i < caderno.paginas; i += 1) {
        const secoes = [];
        if (i === 0) {
            secoes.push(secao('cabeca', [
                texto('Folhas de caligrafia', { tamanho: 'LG', peso: 'FORTE', cor: 'PRINCIPAL_ESCURO' }),
                texto('Escreva devagar, respeitando a linha de base. Repita a mesma letra até o traço ficar automático.', { tamanho: 'XS', cor: 'TEXTO_SUAVE', espacoAcimaMm: 1 }),
                separador({ estilo: 'LINHA', cor: 'DESTAQUE', espessuraMm: 0.4, espacoMm: 2.5 }),
            ], { peso: 0 }));
        }
        secoes.push(secao('guias', [
            blocoCaligrafia({
                linhas,
                alturaLinhaMm: alturaLinha,
                estilo: caderno.estiloCaligrafia,
                cor: 'TEXTO',
                corGuia: 'LINHA',
            }),
        ], { peso: 1 }));
        paginas.push(paginaDeCaderno(ctx, caderno, i, caderno.paginas, secoes, {
            rotuloIndice: i === 0 ? 'Folhas de caligrafia' : undefined,
        }));
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Partitura                                                           */
/* ------------------------------------------------------------------ */
function paginasDePartitura(ctx, caderno) {
    const altura = alturaUtil(ctx, false);
    const espacamentoLinha = 2;
    const alturaSistema = espacamentoLinha * 4 + 1;
    const espacoEntre = 7;
    const sistemas = Math.max(3, Math.floor(altura / (alturaSistema + espacoEntre)));
    const paginas = [];
    for (let i = 0; i < caderno.paginas; i += 1) {
        const secoes = [];
        if (i === 0) {
            secoes.push(secao('cabeca', [
                texto('Pauta musical', { tamanho: 'LG', peso: 'FORTE', cor: 'PRINCIPAL_ESCURO' }),
                texto('Pentagramas em branco, como no papel de manuscrito: escreva a clave e a fórmula de compasso à mão.', { tamanho: 'XS', cor: 'TEXTO_SUAVE', espacoAcimaMm: 1 }),
                separador({ estilo: 'LINHA', cor: 'DESTAQUE', espessuraMm: 0.4, espacoMm: 2.5 }),
            ], { peso: 0 }));
        }
        secoes.push(secao('pentagramas', [
            blocoPartitura({
                sistemas,
                espacamentoLinhaMm: espacamentoLinha,
                espacoEntreSistemasMm: espacoEntre,
                cor: 'TEXTO',
                barraFinal: true,
            }),
        ], { peso: 1 }));
        paginas.push(paginaDeCaderno(ctx, caderno, i, caderno.paginas, secoes, {
            rotuloIndice: i === 0 ? 'Pauta musical' : undefined,
        }));
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Caderno de desenho                                                  */
/* ------------------------------------------------------------------ */
function paginasDeDesenho(ctx, caderno) {
    const total = Math.max(1, caderno.imagens.length + caderno.paginasEmBranco);
    const paginas = [];
    for (let i = 0; i < total; i += 1) {
        const imagem = caderno.imagens[i];
        const secoes = [];
        if (i === 0 && imagem) {
            secoes.push(secao('cabeca', [
                texto(caderno.titulo, { tamanho: 'LG', peso: 'FORTE', cor: 'PRINCIPAL_ESCURO' }),
                texto(`${caderno.imagens.length} imagem(ns) e ${caderno.paginasEmBranco} página(s) em branco.`, {
                    tamanho: 'XS',
                    cor: 'TEXTO_SUAVE',
                    espacoAcimaMm: 1,
                }),
                separador({ estilo: 'LINHA', cor: 'DESTAQUE', espessuraMm: 0.4, espacoMm: 2.5 }),
            ], { peso: 0 }));
        }
        if (imagem) {
            secoes.push(secao('figura', [
                blocoImagem(imagem.url, {
                    proporcao: imagem.proporcao,
                    ajuste: 'CONTER',
                    legenda: imagem.legenda || undefined,
                    moldura: true,
                    flexivel: true,
                }),
            ], { peso: 1 }));
        }
        else {
            secoes.push(secao('branco', [
                caderno.molduraDesenho
                    ? caixa({
                        conteudo: [],
                        fundo: 'NENHUM',
                        borda: 'SUAVE',
                        raioMm: 2,
                        preenchimentoMm: 0,
                        flexivel: true,
                    })
                    : espaco(0, true),
            ], { peso: 1 }));
        }
        paginas.push(paginaDeCaderno(ctx, caderno, i, total, secoes, {
            rotuloIndice: i === 0 ? caderno.titulo : undefined,
        }));
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Fichas: receitas, treino e estudos                                  */
/* ------------------------------------------------------------------ */
function paginasDeFichas(ctx, caderno) {
    const altura = alturaUtil(ctx, true);
    const alturaLinha = caderno.alturaLinhaMm;
    const paginas = [];
    const alturaFicha = caderno.tipo === 'RECEITAS' || caderno.tipo === 'REUNIOES'
        ? Math.round(altura * 0.92)
        : Math.round(altura * 0.46);
    const porPagina = Math.max(1, Math.floor(altura / alturaFicha));
    for (let i = 0; i < caderno.paginas; i += 1) {
        const secoes = [];
        if (i === 0) {
            secoes.push(secaoTitulo(caderno.titulo, DESCRICAO_FICHA[caderno.tipo]));
        }
        secoes.push(secao('fichas', Array.from({ length: porPagina }, () => fichaDoTipo(caderno, alturaLinha)), { peso: 1, gapMm: 4 }));
        paginas.push(paginaDeCaderno(ctx, caderno, i, caderno.paginas, secoes, {
            rotuloIndice: i === 0 ? caderno.titulo : undefined,
        }));
    }
    return paginas;
}
const DESCRICAO_FICHA = {
    RECEITAS: 'Ingredientes e modo de preparo, para as receitas que você sempre volta a fazer.',
    TREINO: 'Exercícios, séries, repetições e carga de cada treino.',
    ESTUDOS: 'Matéria, resumo e dúvidas de cada sessão de estudo.',
    REUNIOES: 'Uma página por reunião: quem estava, o que foi decidido e o que ficou para fazer.',
    PACIENTES: 'Ficha de atendimento com queixa, procedimento, evolução e retorno.',
};
function fichaDoTipo(caderno, alturaLinha) {
    if (caderno.tipo === 'RECEITAS') {
        return caixa({
            conteudo: [
                campos([
                    { rotulo: 'Receita', linhas: 1 },
                    { rotulo: 'Tempo', linhas: 1 },
                    { rotulo: 'Rende', linhas: 1 },
                ], { alturaLinhaMm: alturaLinha }),
                blocoLinhas({
                    alturaLinhaMm: alturaLinha,
                    rotulo: 'Ingredientes',
                    rotuloCor: 'DESTAQUE_TEXTO',
                    flexivel: true,
                }),
                blocoLinhas({
                    alturaLinhaMm: alturaLinha,
                    rotulo: 'Modo de preparo',
                    rotuloCor: 'DESTAQUE_TEXTO',
                    flexivel: true,
                }),
            ],
            fundo: 'SUAVE',
            raioMm: 3,
            preenchimentoMm: 4,
            flexivel: true,
        });
    }
    if (caderno.tipo === 'TREINO') {
        return caixa({
            conteudo: [
                campos([
                    { rotulo: 'Treino', linhas: 1 },
                    { rotulo: 'Data / duração', linhas: 1 },
                ], { alturaLinhaMm: alturaLinha }),
                tabela(['Exercício', 'Séries', 'Reps', 'Carga'], {
                    larguras: [0.46, 0.16, 0.16, 0.22],
                    linhas: 7,
                    alturaLinhaMm: Math.max(6, alturaLinha),
                    flexivel: true,
                    borda: 'TRACEJADA',
                }),
            ],
            fundo: 'SUAVE',
            raioMm: 3,
            preenchimentoMm: 4,
            flexivel: true,
        });
    }
    if (caderno.tipo === 'REUNIOES') {
        const pauta = caderno.itens.filter((item) => item.trim());
        return caixa({
            conteudo: [
                campos([
                    { rotulo: 'Assunto', linhas: 1 },
                    { rotulo: 'Data e hora', linhas: 1 },
                    { rotulo: 'Quem participou', linhas: 1 },
                ], { alturaLinhaMm: alturaLinha }),
                pauta.length > 0
                    ? checklist(pauta, {
                        marcador: 'QUADRADO',
                        alturaLinhaMm: Math.max(6, alturaLinha + 1),
                        rotulo: 'Pauta',
                        rotuloCor: 'DESTAQUE_TEXTO',
                    })
                    : blocoLinhas({
                        alturaLinhaMm: alturaLinha,
                        rotulo: 'Pauta',
                        rotuloCor: 'DESTAQUE_TEXTO',
                        flexivel: true,
                    }),
                blocoLinhas({
                    alturaLinhaMm: alturaLinha,
                    rotulo: 'Decisões e o que ficou combinado',
                    rotuloCor: 'DESTAQUE_TEXTO',
                    flexivel: true,
                }),
            ],
            fundo: 'SUAVE',
            raioMm: 3,
            preenchimentoMm: 4,
            flexivel: true,
        });
    }
    if (caderno.tipo === 'PACIENTES') {
        return caixa({
            conteudo: [
                campos([
                    { rotulo: 'Data', linhas: 1 },
                    { rotulo: 'Nome', linhas: 1 },
                    { rotulo: 'Telefone', linhas: 1 },
                    { rotulo: 'Queixa ou procedimento', linhas: 1 },
                ], { alturaLinhaMm: alturaLinha }),
                blocoLinhas({
                    alturaLinhaMm: alturaLinha,
                    rotulo: 'Evolução e observações',
                    rotuloCor: 'DESTAQUE_TEXTO',
                    flexivel: true,
                }),
                campos([{ rotulo: 'Retorno', linhas: 1 }, { rotulo: 'Valor', linhas: 1 }], {
                    alturaLinhaMm: Math.max(6, alturaLinha),
                }),
            ],
            fundo: 'SUAVE',
            raioMm: 3,
            preenchimentoMm: 4,
            flexivel: true,
        });
    }
    return caixa({
        conteudo: [
            campos([
                { rotulo: 'Matéria', linhas: 1 },
                { rotulo: 'Tema', linhas: 1 },
                { rotulo: 'Data', linhas: 1 },
            ], { alturaLinhaMm: alturaLinha }),
            blocoLinhas({ alturaLinhaMm: alturaLinha, rotulo: 'Resumo', rotuloCor: 'DESTAQUE_TEXTO', flexivel: true }),
            blocoLinhas({ alturaLinhaMm: alturaLinha, rotulo: 'Dúvidas e próximos passos', flexivel: true }),
        ],
        fundo: 'SUAVE',
        raioMm: 3,
        preenchimentoMm: 4,
        flexivel: true,
    });
}
/* ------------------------------------------------------------------ */
/* Rastreador de hábitos                                               */
/* ------------------------------------------------------------------ */
function paginasDeHabitos(ctx, caderno) {
    const altura = alturaUtil(ctx, true);
    const usarDuasTabelas = PAPEIS[ctx.formato].estreito;
    const dias = usarDuasTabelas ? 16 : 31;
    const alturaLinha = Math.max(7, caderno.alturaLinhaMm + 2);
    const linhasPorBloco = Math.max(6, Math.floor((altura / (usarDuasTabelas ? 2 : 1) - 10) / alturaLinha));
    const paginas = [];
    const colunas = ['Hábito', ...Array.from({ length: dias }, (_, i) => String(i + 1))];
    const larguras = [0.24, ...Array.from({ length: dias }, () => 0.76 / dias)];
    for (let i = 0; i < caderno.paginas; i += 1) {
        const secoes = [];
        if (i === 0) {
            secoes.push(secaoTitulo('Rastreador de hábitos', 'Escreva os hábitos e marque cada dia cumprido.'));
        }
        if (usarDuasTabelas) {
            secoes.push(secao('habitos-a', [
                tabela(colunas, { larguras, linhas: linhasPorBloco, alturaLinhaMm: alturaLinha, flexivel: true, borda: 'SUAVE', rotulo: 'Dias 1 a 16' }),
            ], { peso: 1 }), secao('habitos-b', [
                tabela(['Hábito', ...Array.from({ length: 15 }, (_, i2) => String(i2 + 17))], {
                    larguras: [0.24, ...Array.from({ length: 15 }, () => 0.76 / 15)],
                    linhas: linhasPorBloco,
                    alturaLinhaMm: alturaLinha,
                    flexivel: true,
                    borda: 'SUAVE',
                    rotulo: 'Dias 17 a 31',
                }),
            ], { peso: 1 }));
        }
        else {
            secoes.push(secao('habitos', [
                tabela(colunas, { larguras, linhas: linhasPorBloco, alturaLinhaMm: alturaLinha, flexivel: true, borda: 'SUAVE' }),
            ], { peso: 1 }));
        }
        paginas.push(paginaDeCaderno(ctx, caderno, i, caderno.paginas, secoes, {
            rotuloIndice: i === 0 ? 'Rastreador de hábitos' : undefined,
        }));
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Checklist de tarefas e horário escolar                              */
/* ------------------------------------------------------------------ */
/**
 * Checklist de tarefas: as tarefas escritas pelo usuário vêm impressas nas
 * primeiras linhas e o restante da página fica em branco para ele acrescentar.
 */
function paginasDePlanejamento(ctx, caderno) {
    const altura = alturaUtil(ctx, true);
    const alturaLinha = Math.max(7, caderno.alturaLinhaMm + 1);
    const linhasPorPagina = Math.max(6, Math.floor((altura - 8) / alturaLinha));
    const paginas = [];
    for (let i = 0; i < caderno.paginas; i += 1) {
        const inicio = i * linhasPorPagina;
        const tarefas = caderno.itens.slice(inicio, inicio + linhasPorPagina);
        const vazias = Math.max(0, linhasPorPagina - tarefas.length);
        const celulas = [
            ...tarefas.map((tarefa) => [tarefa, '', '']),
            ...Array.from({ length: vazias }, () => ['', '', '']),
        ];
        const secoes = [];
        if (i === 0) {
            const restantes = Math.max(0, caderno.itens.length - linhasPorPagina);
            secoes.push(secaoTitulo(caderno.titulo, caderno.itens.length > 0
                ? `${caderno.itens.length} tarefa(s) na lista${restantes > 0 ? `, ${restantes} seguem nas próximas páginas` : ''}.`
                : 'Escreva as tarefas e vá marcando conforme concluir.'));
        }
        secoes.push(secao('tarefas', [
            tabela(['Tarefa', 'Prazo', '✓'], {
                larguras: [0.68, 0.18, 0.14],
                linhas: linhasPorPagina,
                celulas,
                alturaLinhaMm: alturaLinha,
                flexivel: true,
                borda: 'SUAVE',
            }),
        ], { peso: 1 }));
        paginas.push(paginaDeCaderno(ctx, caderno, i, caderno.paginas, secoes, {
            rotuloIndice: i === 0 ? caderno.titulo : undefined,
        }));
    }
    return paginas;
}
/** Grade da semana com as aulas, mais a tabela de matérias e professores. */
function paginasDeHorario(ctx, caderno) {
    const altura = alturaUtil(ctx, true);
    const alturaLinha = Math.max(7, caderno.alturaLinhaMm + 1);
    const dias = caderno.diasDaGrade.length > 0 ? caderno.diasDaGrade : DIAS_DA_GRADE;
    const horarios = caderno.itens.length > 0 ? caderno.itens : HORARIOS_ESCOLARES;
    const larguraDia = 0.78 / dias.length;
    const colunas = ['Horário', ...dias];
    const larguras = [0.22, ...dias.map(() => larguraDia)];
    const alturaGrade = Math.round(altura * 0.58);
    const linhasGrade = Math.max(5, Math.floor((alturaGrade - 8) / alturaLinha));
    const linhasMaterias = Math.max(4, Math.floor((altura - alturaGrade - 26) / alturaLinha));
    const paginas = [];
    for (let i = 0; i < caderno.paginas; i += 1) {
        const secoes = [];
        if (i === 0) {
            secoes.push(secaoTitulo('Horário escolar', 'Aulas da semana e contatos das matérias.'));
        }
        secoes.push(secao('grade', [
            tabela(colunas, {
                larguras,
                linhas: linhasGrade,
                celulas: Array.from({ length: linhasGrade }, (_, linha) => [
                    horarios[linha] ?? '',
                    ...dias.map(() => ''),
                ]),
                alturaLinhaMm: alturaLinha,
                flexivel: true,
                borda: 'SUAVE',
            }),
        ], { peso: 0, alturaMm: alturaGrade, gapMm: 0 }));
        secoes.push(secao('materias', [
            tabela(['Matéria', 'Professor(a)', 'Sala'], {
                larguras: [0.4, 0.36, 0.24],
                linhas: linhasMaterias,
                celulas: Array.from({ length: linhasMaterias }, (_, linha) => [
                    caderno.materias[linha] ?? '',
                    '',
                    '',
                ]),
                alturaLinhaMm: alturaLinha,
                flexivel: true,
                borda: 'SUAVE',
                rotulo: 'Matérias e professores',
            }),
        ], { peso: 1 }));
        paginas.push(paginaDeCaderno(ctx, caderno, i, caderno.paginas, secoes, {
            rotuloIndice: i === 0 ? 'Horário escolar' : undefined,
        }));
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Biblioteca de prompts de IA                                         */
/* ------------------------------------------------------------------ */
/**
 * Caderno de prompts: primeiro o guia impresso (o que é IA, o que é prompt,
 * anatomia, métodos e erros comuns) e depois fichas para registrar os prompts
 * que o dono da agenda testou e deram certo.
 */
function paginasDePrompts(ctx, caderno) {
    const altura = alturaUtil(ctx, false);
    const alturaLinha = caderno.alturaLinhaMm;
    const paginas = [];
    const indicePorPagina = Math.max(1, Math.floor((altura + 4) / (alturaLinha * 24)));
    const totalGuias = Math.max(1, Math.ceil(GUIA_PROMPTS.length / indicePorPagina));
    // --- guia impresso ---
    for (let i = 0; i < totalGuias; i += 1) {
        const recorte = GUIA_PROMPTS.slice(i * indicePorPagina, (i + 1) * indicePorPagina);
        const secoes = [
            secaoTitulo(i === 0 ? 'Guia de IA e prompts' : 'Guia de IA e prompts (continuação)', 'Material de apoio para usar IA com método.'),
        ];
        for (const parte of recorte) {
            secoes.push(secao(`guia-${parte.id}`, [
                texto(parte.titulo, { tamanho: 'MD', peso: 'FORTE', cor: 'PRINCIPAL_ESCURO' }),
                ...parte.paragrafos.map((paragrafo) => texto(paragrafo, { tamanho: 'XS', cor: 'TEXTO', espacoAcimaMm: 1 })),
                ...(parte.itens ?? []).map((item) => texto(`· ${item}`, { tamanho: 'XS', cor: 'TEXTO_SUAVE', espacoAcimaMm: 0.6 })),
                separador({ estilo: 'TRACEJADA', cor: 'LINHA', espacoMm: 2.5 }),
            ], { peso: 1, gapMm: 0.5 }));
        }
        paginas.push(paginaDeCaderno(ctx, caderno, paginas.length, caderno.paginas + totalGuias, secoes, {
            rotuloIndice: i === 0 ? 'Guia de IA e prompts' : undefined,
        }));
    }
    // --- fichas para registrar prompts ---
    const alturaFicha = Math.round(altura * 0.46);
    const porPagina = Math.max(1, Math.floor(altura / alturaFicha));
    for (let i = 0; i < caderno.paginas; i += 1) {
        const secoes = i === 0
            ? [secaoTitulo('Meus prompts', 'Registre o prompt como funcionou, com o modelo e o resultado.')]
            : [];
        secoes.push(secao('fichas-prompt', Array.from({ length: porPagina }, () => caixa({
            conteudo: [
                campos([
                    { rotulo: 'Título', linhas: 1, mascara: 'ex.: Revisar texto sem perder meu estilo' },
                    { rotulo: 'Categoria', linhas: 1, mascara: 'escrita · estudos · trabalho · negócios · pessoal' },
                    { rotulo: 'Objetivo', linhas: 1 },
                ], { alturaLinhaMm: alturaLinha, rotuloAbaixo: true }),
                blocoLinhas({
                    alturaLinhaMm: alturaLinha,
                    rotulo: 'Prompt (como funcionou)',
                    rotuloCor: 'DESTAQUE_TEXTO',
                    flexivel: true,
                    ...enfeitesDePauta(ctx.escrita),
                }),
                blocoLinhas({
                    alturaLinhaMm: alturaLinha,
                    rotulo: 'Modelo, versão, resultado e o que ajustei',
                    flexivel: true,
                }),
            ],
            fundo: 'SUAVE',
            raioMm: 3,
            preenchimentoMm: 4,
            flexivel: true,
        })), { peso: 1, gapMm: 4 }));
        paginas.push(paginaDeCaderno(ctx, caderno, paginas.length, caderno.paginas + totalGuias, secoes, {
            rotuloIndice: i === 0 ? 'Meus prompts' : undefined,
        }));
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Folhas para escrever                                                */
/* ------------------------------------------------------------------ */
/**
 * Caderno só de folhas de escrita.
 *
 * O usuário monta o caderno em blocos: 20 páginas pautadas, 10 pontilhadas,
 * 5 com linha tracejada... Cada bloco vira N páginas com o estilo escolhido e o
 * assistente mostra o número de páginas de cada item.
 */
function paginasDeFolhas(ctx, caderno) {
    const blocos = caderno.blocosFolhas.length > 0
        ? caderno.blocosFolhas
        : [{ estilo: caderno.estiloPauta, paginas: Math.max(1, caderno.paginas) }];
    const total = totalDeFolhas(blocos);
    const paginas = [];
    let indice = 0;
    for (const bloco of blocos) {
        const quantidade = Math.max(1, Math.round(bloco.paginas));
        const alturaLinha = bloco.alturaLinhaMm ?? caderno.alturaLinhaMm;
        for (let i = 0; i < quantidade; i += 1) {
            const secoes = [];
            if (indice === 0) {
                secoes.push(secaoTitulo(caderno.titulo, 'Folhas para escrever, com a pauta escolhida para cada trecho do caderno.'));
            }
            if (caderno.mostrarData) {
                secoes.push(secao('data', [
                    campos([{ rotulo: 'Data', linhas: 1 }, { rotulo: 'Assunto', linhas: 1 }], {
                        alturaLinhaMm: Math.max(6, alturaLinha),
                    }),
                ], { alturaMm: 16, peso: 0 }));
            }
            secoes.push(secao('pauta', [
                blocoLinhas({
                    ...enfeitesDePauta(ctx.escrita),
                    alturaLinhaMm: alturaLinha,
                    espacamentoMm: ctx.espacamentoMm,
                    estilo: bloco.estilo,
                    traco: bloco.traco,
                    flexivel: true,
                    colunas: PAPEIS[ctx.formato].largo ? 2 : 1,
                }),
            ], { peso: 1 }));
            paginas.push(paginaDeCaderno(ctx, caderno, indice, total, secoes, {
                rotuloIndice: indice === 0 ? caderno.titulo : undefined,
            }));
            indice += 1;
        }
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Entrada principal                                                   */
/* ------------------------------------------------------------------ */
export function construirPaginasDoCaderno(ctx, caderno) {
    if (!caderno.visivel)
        return [];
    switch (caderno.tipo) {
        case 'CONTATOS':
        case 'SENHAS':
        case 'FINANCAS':
        case 'CONVIDADOS':
        case 'PROJETOS':
        case 'ORCAMENTOS':
        case 'ESTOQUE':
            return paginasDeLista(ctx, caderno);
        case 'EMERGENCIA':
            return paginasDeEmergencia(ctx, caderno);
        case 'ORACOES':
            return paginasDeOracoes(ctx, caderno);
        case 'LEITURA_BIBLIA':
            return paginasDeLeitura(ctx, caderno);
        case 'PAUTADO':
            return paginasDePauta(ctx, caderno);
        case 'CALIGRAFIA':
            return paginasDeCaligrafia(ctx, caderno);
        case 'PARTITURA':
            return paginasDePartitura(ctx, caderno);
        case 'DESENHO':
            return paginasDeDesenho(ctx, caderno);
        case 'RECEITAS':
        case 'TREINO':
        case 'ESTUDOS':
        case 'REUNIOES':
        case 'PACIENTES':
            return paginasDeFichas(ctx, caderno);
        case 'HABITOS':
            return paginasDeHabitos(ctx, caderno);
        case 'PLANEJAMENTO':
            return paginasDePlanejamento(ctx, caderno);
        case 'HORARIO':
            return paginasDeHorario(ctx, caderno);
        case 'PROMPTS':
            return paginasDePrompts(ctx, caderno);
        case 'FOLHAS':
            return paginasDeFolhas(ctx, caderno);
        default:
            return [];
    }
}
/** Painel de configuração usado pelo assistente, para não duplicar rótulos. */
export function opcoesDoCaderno(tipo, formato) {
    const papel = PAPEIS[formato];
    return { especificacao: ESPECIFICACOES[tipo], papel };
}
//# sourceMappingURL=cadernos.js.map